import { type ReactElement } from 'react';
import { Alert, Box, Card, CardContent, EmptyState, Skeleton, Text, DeliveryIcon } from '@citibox-panel/ui';
import { ReadErrorStrip } from '../../../components/ReadErrorStrip';
import { useDownloadQrBatch } from '../hooks/useDownloadQrBatch';
import type { QrBatch, QrBatchSeams } from '../lib/qrBatch';
import { BatchesTable } from './BatchesTable';

export interface BatchesCardProps extends Pick<QrBatchSeams, 'downloadQrBatch'> {
  // The list is read once, by the page, because the form beside this card waits on it too.
  batches: QrBatch[] | undefined;
  isPending: boolean;
  isError: boolean;
  isGenerating: boolean;
  onRetry: () => void;
  timeZone: string | null;
}

// Fixed panel copy: the server's own `message`/`detail` never reaches the DOM.
// TODO: i18n — copy en ES hasta Fase 1.
const READ_ERROR = 'No se ha podido cargar la lista de lotes.';
const DOWNLOAD_ERROR = 'No se ha podido descargar el fichero del lote. Inténtalo de nuevo.';
const POLLING_NOTE = 'Se actualiza sola cada pocos segundos mientras haya un lote generándose.';
const SKELETON_ROWS = 3;

const countLabel = (count: number): string => (count === 1 ? '1 lote' : `${String(count)} lotes`);

export function BatchesCard({
  batches,
  isPending,
  isError,
  isGenerating,
  onRetry,
  timeZone,
  downloadQrBatch,
}: BatchesCardProps): ReactElement {
  const { download, downloadingId, failedId } = useDownloadQrBatch(downloadQrBatch);

  return (
    <Card variant="elevation" elevation={4} sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <DeliveryIcon fontSize="small" />
          <Text variant="subtitle2" component="h3" bold>
            Lotes
          </Text>
          {/* Mounted in every state, so the region exists BEFORE its text arrives. */}
          <Box role="status" sx={{ ml: 1 }}>
            {batches !== undefined && !isError && (
              <Text variant="body2" component="span">
                {`· ${countLabel(batches.length)}`}
              </Text>
            )}
          </Box>
        </Box>
        {isGenerating && (
          <Box sx={{ mb: 1 }}>
            <Text variant="body2">{POLLING_NOTE}</Text>
          </Box>
        )}
        {isError && <ReadErrorStrip message={READ_ERROR} onRetry={onRetry} />}
        {!isError && isPending && (
          <Box aria-label="Cargando los lotes">
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <Skeleton key={index} variant="text" height={36} />
            ))}
          </Box>
        )}
        {!isError && batches !== undefined && batches.length === 0 && (
          <EmptyState
            title="Todavía no hay lotes"
            description="Solicita el primero con el formulario de arriba"
          />
        )}
        {!isError && batches !== undefined && batches.length > 0 && (
          <BatchesTable
            batches={batches}
            timeZone={timeZone}
            downloadingId={downloadingId}
            onDownload={download}
          />
        )}
        {failedId !== null && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{DOWNLOAD_ERROR}</Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
